import { Phone, Menu, X, ChevronDown } from "lucide-react";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { SITE } from "@/lib/siteConfig";
import { SERVICE_PAGES } from "@/lib/servicePages";
import { trackConversion } from "@/lib/gtag";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl border-b border-black/10 shadow-sm"
          : "bg-white border-b border-black/5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20 gap-4">
          {/* Logo */}
          <Link
            to="/"
            data-testid="brand-logo"
            className="flex items-center gap-2 group flex-shrink-0"
          >
            <div className="w-9 h-9 bg-[#0A0A0A] text-white font-display font-black flex items-center justify-center text-sm tracking-tighter">
              SOS
            </div>
            <div className="leading-tight">
              <div className="font-display font-black text-sm sm:text-base tracking-tight">
                SERRURIER EXPRESS
              </div>
              <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-red-600 font-bold">
                24h/24 · 7j/7
              </div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-7 flex-1 justify-center">
            <Link
              to="/"
              data-testid="nav-accueil"
              className="text-sm font-bold uppercase tracking-[0.05em] text-slate-700 hover:text-red-600 transition"
            >
              Accueil
            </Link>

            {/* Services dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button
                data-testid="nav-services"
                className="inline-flex items-center gap-1 text-sm font-bold uppercase tracking-[0.05em] text-slate-700 hover:text-red-600 transition"
              >
                Services
                <ChevronDown className={`w-3.5 h-3.5 transition ${servicesOpen ? "rotate-180" : ""}`} />
              </button>
              {servicesOpen && (
                <div
                  data-testid="services-dropdown"
                  className="absolute top-full left-1/2 -translate-x-1/2 pt-3 w-72"
                >
                  <div className="bg-white border border-black/10 shadow-2xl py-2">
                    {SERVICE_PAGES.map((s) => (
                      <Link
                        key={s.slug}
                        to={s.path}
                        data-testid={`nav-service-${s.slug}`}
                        className="block px-5 py-3 hover:bg-red-50 hover:text-red-600 transition"
                      >
                        <div className="font-display font-bold text-sm">{s.short}</div>
                        <div className="text-[11px] text-slate-500 uppercase tracking-[0.1em] mt-0.5">
                          → {s.title.length > 38 ? s.title.slice(0, 38) + "…" : s.title}
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/#zones"
              data-testid="nav-zones"
              className="text-sm font-bold uppercase tracking-[0.05em] text-slate-700 hover:text-red-600 transition"
            >
              Zones
            </Link>
            <Link
              to="/blog"
              data-testid="nav-blog"
              className="text-sm font-bold uppercase tracking-[0.05em] text-slate-700 hover:text-red-600 transition"
            >
              Blog
            </Link>
            <Link
              to="/#faq"
              data-testid="nav-faq"
              className="text-sm font-bold uppercase tracking-[0.05em] text-slate-700 hover:text-red-600 transition"
            >
              FAQ
            </Link>
            <Link
              to="/#contact"
              data-testid="nav-devis"
              className="text-sm font-bold uppercase tracking-[0.05em] text-slate-700 hover:text-red-600 transition"
            >
              Devis
            </Link>
          </nav>

          {/* Phone CTA */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <a
              href={SITE.phoneHref}
              onClick={() => trackConversion("phone_click_header")}
              data-testid="call-cta-header"
              className="hidden sm:inline-flex items-center gap-2.5 bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 sm:py-3 font-bold tracking-tight text-sm sm:text-base transition-all hover:-translate-y-0.5 shadow-[0_4px_0_#991B1B] hover:shadow-[0_2px_0_#991B1B]"
            >
              <Phone className="w-4 h-4" strokeWidth={2.5} />
              <span className="font-display tracking-tight">{SITE.phone}</span>
            </a>
            <a
              href={SITE.phoneHref}
              onClick={() => trackConversion("phone_click_header_mobile")}
              data-testid="call-cta-header-mobile"
              className="sm:hidden inline-flex items-center justify-center bg-red-600 text-white w-11 h-11 pulse-ring"
              aria-label="Appeler maintenant"
            >
              <Phone className="w-5 h-5" strokeWidth={2.5} />
            </a>
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden w-11 h-11 flex items-center justify-center border border-black/10"
              onClick={() => setOpen((v) => !v)}
              aria-label="Menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden py-4 border-t border-black/10" data-testid="mobile-menu">
            <nav className="flex flex-col gap-1">
              <Link to="/" onClick={() => setOpen(false)} className="px-2 py-3 text-base font-bold border-b border-black/5">
                Accueil
              </Link>
              <div className="px-2 py-3 text-[11px] uppercase tracking-[0.2em] font-bold text-red-600">
                Services
              </div>
              {SERVICE_PAGES.map((s) => (
                <Link
                  key={s.slug}
                  to={s.path}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${s.slug}`}
                  className="px-4 py-3 text-base font-semibold text-slate-800 hover:text-red-600 border-b border-black/5"
                >
                  → {s.short}
                </Link>
              ))}
              <Link to="/#zones" onClick={() => setOpen(false)} className="px-2 py-3 mt-2 text-base font-bold border-b border-black/5">
                Zones
              </Link>
              <Link to="/blog" onClick={() => setOpen(false)} data-testid="mobile-nav-blog" className="px-2 py-3 text-base font-bold border-b border-black/5">
                Blog
              </Link>
              <Link to="/#faq" onClick={() => setOpen(false)} className="px-2 py-3 text-base font-bold border-b border-black/5">
                FAQ
              </Link>
              <Link to="/#contact" onClick={() => setOpen(false)} className="px-2 py-3 text-base font-bold">
                Devis
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
